import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import AuthUtil from '../../redux/AuthUtil';
import { TRANSACTION } from '../global/Server/endpoints';
import AuditLogList from '../employeeSession/shared/AuditLogList';
import { TOKENS } from '../employeeSession/theme';

// Transaction toolbar → "My Transfer History": the acting employee's
// transfers (money and cash left) on the shift they're clocked into — sent,
// accepted, rejected, cancelled, by either side. The server picks the open
// shift; userId is the navbar-switched employee (same company only).
export default function TransactionHistoryDialog({
  open,
  onClose,
  userId,
  customers,
  employees,
}) {
  const fullScreen = useMediaQuery('(max-width: 800px)');
  const [state, setState] = React.useState({
    loading: false,
    error: null,
    clockedIn: true,
    logs: [],
  });

  React.useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    AuthUtil.postRequest(TRANSACTION.SHIFT_HISTORY, { userId })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || {};
        if (!data.success) {
          setState({
            loading: false,
            error: data.message || 'Could not load your history.',
            clockedIn: true,
            logs: [],
          });
          return;
        }
        setState({
          loading: false,
          error: null,
          clockedIn: data.clockedIn !== false,
          logs: data.logs || [],
        });
      })
      .catch(() => {
        if (cancelled) return;
        setState({
          loading: false,
          error: 'Could not load your history.',
          clockedIn: true,
          logs: [],
        });
      });
    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  let body;
  if (state.loading) {
    body = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress size={28} />
      </Box>
    );
  } else if (state.error) {
    body = (
      <Typography sx={{ py: 4, textAlign: 'center', color: TOKENS.red700 }}>
        {state.error}
      </Typography>
    );
  } else if (!state.clockedIn) {
    body = (
      <Typography sx={{ py: 4, textAlign: 'center', color: TOKENS.slate500 }}>
        You're not clocked in. Clock in to see your transfers.
      </Typography>
    );
  } else {
    body = (
      <AuditLogList
        auditLog={state.logs}
        customers={customers}
        employees={employees}
        emptyText="No transfers yet."
        hideFilters
      />
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${TOKENS.slate200}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{ fontSize: 18, fontWeight: 700, color: TOKENS.slate900 }}
          >
            My Transfer History
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ background: TOKENS.slate50, p: 2 }}>
        {body}
      </DialogContent>
    </Dialog>
  );
}
